import {ListType, MarkdownEditor} from '@frontapp/editor-html';
import {isUndefined, noop, pick} from 'lodash';
import React, {Component} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {trySetLinkUrlFromSelectedText} from '../../../../helpers/editorHelpers';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {MarkdownStyle} from '../../../common/editor/style/markdownStyle';
import {MarkdownFormattingControls} from './formattingConstants';
import {FormattingLinkDialog} from './formattingLinkDialog';
import {FormattingToolbar} from './formattingToolbar';

/*
 * Props.
 */

interface FormattingMarkdownToolbarContainerProps {
  isVisible: boolean;
  isKeyboardShortcutsEnabled: boolean;
  markdownEditor: MarkdownEditor;
}

/*
 * Component.
 */

interface FormattingMarkdownToolbarContainerState {
  markdownEditor: MarkdownEditor;
  isLinkDialogOpen: boolean;
}

export class FormattingMarkdownToolbarContainer extends Component<
  FormattingMarkdownToolbarContainerProps,
  FormattingMarkdownToolbarContainerState
> {
  constructor(props: FormattingMarkdownToolbarContainerProps) {
    super(props);

    this.state = {
      markdownEditor: props.markdownEditor,
      isLinkDialogOpen: false,
    };
  }

  /** Convenience helper that performs a formatting action. */
  private applyFormatting(callback: (markdownEditor: MarkdownEditor) => void) {
    const {markdownEditor} = this.state;
    callback(markdownEditor);
  }

  /*
   * Event handlers.
   */

  private readonly onStyleChange: HandlerOf<MarkdownStyle> = (newStyle) =>
    this.applyFormatting((markdownEditor) => markdownEditor.services.style.applyStyle(newStyle));

  private readonly onListTypeChange: HandlerOf<ListType | undefined> = (newListType) =>
    this.applyFormatting((htmlEditor) => htmlEditor.services.list.setType(newListType));

  private readonly onLinkButtonClick: Handler = () => {
    if (trySetLinkUrlFromSelectedText(this.state.markdownEditor.services.link)) {
      return;
    }

    this.setState({isLinkDialogOpen: true});
  };

  private readonly onLinkUrlChange: HandlerOf<string | undefined> = (newUrl) =>
    this.applyFormatting((markdownEditor) => markdownEditor.services.link.setUrl(newUrl));

  private readonly onQuoteButtonClick: Handler = () =>
    this.applyFormatting((markdownEditor) => {
      const service = markdownEditor.services.quote;
      return service.isQuoted ? service.unquote() : service.quote();
    });

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    props: Readonly<FormattingMarkdownToolbarContainerProps>,
    state: Readonly<FormattingMarkdownToolbarContainerState>,
  ): Partial<FormattingMarkdownToolbarContainerState> | null {
    const {markdownEditor} = props;
    return {markdownEditor};
  }

  /*
   * Render.
   */

  render() {
    const {isVisible, isKeyboardShortcutsEnabled} = this.props;
    const {markdownEditor} = this.state;
    const services = pick(markdownEditor.services, ['style', 'link', 'indent', 'quote', 'list']);

    return (
      <>
        <ServiceSubscription source={services}>
          {({style: {style}, link: {url: linkUrl}, quote: {isQuoted}, list: {type: listType}}) => (
            <FormattingToolbar
              isVisible={isVisible}
              isKeyboardShortcutsEnabled={isKeyboardShortcutsEnabled}
              markdownStyle={style}
              onStyleChange={this.onStyleChange}
              listType={listType}
              onListTypeChange={this.onListTypeChange}
              isLinkButtonActive={!isUndefined(linkUrl)}
              onLinkButtonClick={this.onLinkButtonClick}
              isQuoteButtonActive={isQuoted}
              onQuoteButtonClick={this.onQuoteButtonClick}
              controlsToRender={new Set(MarkdownFormattingControls)}
              canIndent={false}
              canOutdent={false}
              onInsertImageUrlInlineClick={noop}
              hasListAutoFormatting={false}
              isImageSelectedInComposer={false}
              onUploadImageInlineClick={noop}
              shouldRenderDropdowns={false}
            />
          )}
        </ServiceSubscription>
        {this.maybeRenderLinkDialog()}
      </>
    );
  }

  private maybeRenderLinkDialog() {
    if (!this.state.isLinkDialogOpen) {
      return null;
    }

    const {markdownEditor} = this.state;
    const {url, selectedText} = markdownEditor.services.link;

    return (
      <FormattingLinkDialog
        link={url}
        onLinkChange={this.onLinkUrlChange}
        onRequestClose={() => this.setState({isLinkDialogOpen: false})}
        selectedText={selectedText}
      />
    );
  }
}
